import { ApolloClient, DocumentNode } from '@apollo/client';
import { useEffect } from 'react';
import { isEqual } from 'lodash';
import { ListDomainsForPageDocument } from '../../graphql/domain.generated';
import { Entity, EntityType } from '../../types.generated';
import { GenericEntityProperties } from '../entity/shared/types';
import usePrevious from '../shared/usePrevious';
import { useDomainsContext } from './DomainsContext';
import EntityRegistry from '../entity/EntityRegistry';

export const NAV_PAGE_SIZE = 50;
export const DOMAIN_NODE_CHILDREN_PAGE_SIZE = 500;
export const DOMAINS_LIST_PAGE_SIZE = 25;
export const ROOT_DOMAINS_PAGE_SIZE = 10;
export const DOMAIN_NAVIGATOR_SCROLL_THRESHOLD = 100;

// Only `listDomainsForPage` is targeted for cache mutations:
//  - `listDomainsForPage` powers the paginated Domains list page (DomainsList) — page size 25.
//  - `listDomains` powers RootDomains with page size 10; no caller passes count:10 so it
//    would never match a cache entry and is excluded.
//  - `listDomainsForNav` uses local accumulated state for infinite scroll, so Apollo cache
//    writes do not apply to it.
const LIST_DOMAINS_DOCUMENTS = [ListDomainsForPageDocument];

const readListDomains = (client: ApolloClient<any>, document: DocumentNode, variables: Record<string, unknown>) => {
    try {
        return client.readQuery({ query: document, variables });
    } catch {
        // The query is not present in the cache for these variables.
        return null;
    }
};

/**
 * Add an entry to the list domains cache.
 */
export const addToListDomainsCache = (client, newDomain, pageSize, parentDomain?: string) => {
    const variables = {
        input: {
            start: 0,
            count: pageSize,
            parentDomain,
        },
    };

    const domainToAdd = {
        id: null,
        type: EntityType.Domain,
        ...newDomain,
        properties: {
            description: null,
            ...(newDomain.properties || {}),
        },
    };

    LIST_DOMAINS_DOCUMENTS.forEach((document) => {
        const currData = readListDomains(client, document, variables);
        if (!currData?.listDomains) {
            return;
        }

        const newDomains = [domainToAdd, ...(currData.listDomains.domains || [])];

        client.writeQuery({
            query: document,
            variables,
            data: {
                listDomains: {
                    ...currData.listDomains,
                    start: currData.listDomains.start ?? 0,
                    count: (currData.listDomains.count || 0) + 1,
                    total: (currData.listDomains.total || 0) + 1,
                    domains: newDomains,
                },
            },
        });
    });
};

export const updateListDomainsCache = (
    client: ApolloClient<any>,
    urn: string,
    id: string | undefined,
    name: string,
    description: string | undefined,
    parentDomain?: string,
) => {
    addToListDomainsCache(
        client,
        {
            urn,
            id: id || null,
            type: EntityType.Domain,
            properties: {
                name,
                description: description || null,
            },
            ownership: null,
            entities: null,
            dataProducts: null,
            children: null,
        },
        1000,
        parentDomain,
    );
};

/**
 * Remove an entry from the list domains cache.
 */
export const removeFromListDomainsCache = (client, urn, page, pageSize, parentDomain?: string) => {
    const variables = {
        input: {
            start: (page - 1) * pageSize,
            count: pageSize,
            parentDomain,
        },
    };

    LIST_DOMAINS_DOCUMENTS.forEach((document) => {
        const currData = readListDomains(client, document, variables);
        if (!currData?.listDomains) {
            return;
        }

        const newDomains = (currData.listDomains.domains || []).filter((domain) => domain.urn !== urn);

        client.writeQuery({
            query: document,
            variables,
            data: {
                listDomains: {
                    ...currData.listDomains,
                    start: currData.listDomains.start || 0,
                    count: (currData.listDomains.count || 1) - 1,
                    total: (currData.listDomains.total || 1) - 1,
                    domains: newDomains,
                },
            },
        });
    });
};

export function useUpdateDomainEntityDataOnChange(entityData: GenericEntityProperties | null, entityType: EntityType) {
    const { setEntityData } = useDomainsContext();
    const previousEntityData = usePrevious(entityData);

    useEffect(() => {
        if (EntityType.Domain === entityType && !isEqual(entityData, previousEntityData)) {
            setEntityData(entityData);
        }
    });
}

export function getParentDomains<T extends Entity>(domain: T, entityRegistry: EntityRegistry) {
    const props = entityRegistry.getGenericEntityProperties(EntityType.Domain, domain);
    return props?.parentDomains?.domains ?? [];
}
